import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { useFormContext } from "react-hook-form";
import type { UseMutationResult } from "react-query";
import type { ApiError } from "@/react/config/CustomErrors";
import { usePunditUserContext } from "@circle-react/contexts";
import type { CommunityMemberBillingInfo } from "@circle-react/types";
import type { BillingInfoContextProps } from "./BillingInfoContextProps";
import type { BillingInfoFormValues } from "./BillingInfoFormValues";
import { BillingInfoContext } from "./useBillingInfoContext";

export interface BillingInfoContextProviderProps
  extends Omit<BillingInfoContextProps, "onSuccess"> {
  billingInfoMutation: UseMutationResult<
    CommunityMemberBillingInfo,
    ApiError,
    BillingInfoFormValues,
    unknown
  >;
  children: ReactNode;
}

export const BillingInfoContextProvider = ({
  communityMember,
  communityMemberBillingInfo,
  children,
  ...props
}: BillingInfoContextProviderProps) => {
  const { setValue } = useFormContext<BillingInfoFormValues>();
  const { currentCommunity } = usePunditUserContext();

  const isBrazilianCommunity = Boolean(
    currentCommunity?.payment_processor
      ?.is_brazilian_account_and_processor_stripe_br,
  );
  const isTaxCollectionEnabled = Boolean(
    currentCommunity?.payment_processor?.is_tax_collection_enabled,
  );

  const setAddress = useCallback(
    (event: StripeAddressElementChangeEvent) => {
      const { value, complete: isComplete } = event;
      const { name, address } = value;

      const shouldDirty =
        name !== communityMember?.name ||
        address.country !== communityMemberBillingInfo?.address_country ||
        address.line1 !== communityMemberBillingInfo?.address_line1 ||
        address.line2 !== communityMemberBillingInfo?.address_line2 ||
        address.city !== communityMemberBillingInfo?.address_city ||
        address.state !== communityMemberBillingInfo?.address_state ||
        address.postal_code !== communityMemberBillingInfo?.address_postal_code;

      const setValueOptions = { shouldDirty };
      setValue("address_country", address.country, setValueOptions);
      setValue("address_line1", address.line1, setValueOptions);
      setValue("address_line2", address.line2, setValueOptions);
      setValue("address_city", address.city, setValueOptions);
      setValue("address_state", address.state, setValueOptions);
      setValue("address_postal_code", address.postal_code, setValueOptions);

      setValue("address_complete", isComplete);
    },
    [communityMember, communityMemberBillingInfo, setValue],
  );

  const [areAddressFieldsReady, setAreAddressFieldsReady] = useState(false);
  const markAddressFieldsReady = useCallback(() => {
    setAreAddressFieldsReady(true);
  }, [setAreAddressFieldsReady]);

  return (
    <BillingInfoContext.Provider
      value={{
        ...props,
        areAddressFieldsReady,
        communityMember,
        communityMemberBillingInfo,
        isBrazilianCommunity,
        isTaxCollectionEnabled,
        markAddressFieldsReady,
        setAddress,
      }}
    >
      {children}
    </BillingInfoContext.Provider>
  );
};
